// DatePicker.jsx

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

// Context
import { useTheme } from 'context/ThemeContext';

// Utilities
import { normalizeDateValue } from 'utilities/DateTime';

// CSS
import './DatePicker.css';

// Icons
import Icon from 'svg/Icon';

// Components
import CalendarPopup from 'components/calendarpopup/CalendarPopup';
import Popup from 'components/popup/Popup';

const clampDigits = (value, maxLength) => {
  const digits = String(value || '').replace(/\D/g, '').slice(0, maxLength);
  return digits;
};

const padStart = (value, length) => {
  if (!value) return '';
  return String(value).padStart(length, '0');
};

const buildDateFromParts = (month, day, year) => {
  if (month.length !== 2 || day.length !== 2 || year.length !== 4) return null;

  const m = Number(month);
  const d = Number(day);
  const y = Number(year);

  if (!Number.isFinite(m) || !Number.isFinite(d) || !Number.isFinite(y)) return null;
  if (m < 1 || m > 12) return null;
  if (d < 1 || d > 31) return null;
  if (y < 1000) return null;

  const candidate = new Date(y, m - 1, d);
  if (candidate.getFullYear() !== y || candidate.getMonth() !== m - 1 || candidate.getDate() !== d) {
    return null;
  }

  return candidate;
};

/**
 * DatePicker
 *
 * Renders a segmented date input (MM / DD / YYYY) with popup calendar selection.
 *
 * @param {Date|string|null} value - Current date value.
 * @param {function} onChange - Called with parsed Date or null when segments change.
 * @param {function} onRawChange - Called with raw segmented text value.
 * @param {function} onBlur - Called when input focus leaves the control.
 * @param {boolean} disabled - Whether the control is disabled.
 * @param {boolean} readOnly - Whether the control is read-only.
 * @param {string} startIconKey - Icon key shown at start.
 * @param {string} startIconColor - Optional start icon color override.
 * @param {number} iconSize - Icon size in pixels.
 * @param {string} className - Optional wrapper class names.
 * @param {boolean} hybrid - Whether to use hybrid transparent background mode.
 * @param {object} style - Optional wrapper style overrides.
 * @param {string} inputClassName - Optional additional class names for segment inputs.
 * @param {object} inputStyle - Optional style overrides for segment inputs.
 * @param {ReactNode|null} endAction - Optional trailing action rendered on the right side.
 * @param {Intl.DateTimeFormatOptions|null} friendlyFormatOptions - Optional locale options for the hybrid idle label.
 * @param {string|number|null} friendlyFontWeight - Optional font-weight for the hybrid idle label.
 *
 * @returns {ReactNode} The rendered component.
 */
const DatePicker = ({
  value = null,
  onChange,
  onRawChange,
  onBlur,
  disabled = false,
  readOnly = false,
  startIconKey = 'calendar_month',
  startIconColor,
  iconSize = 20,
  className = '',
  hybrid = false,
  style = {},
  inputClassName = '',
  inputStyle = {},
  endAction = null,
  friendlyFormatOptions = null,
  friendlyFontWeight = null,
}) => {

  // ----- CONTEXT ----- //

  // Theme Context

  const { theme } = useTheme();

  // ----- STATE ----- //

  // Segment Values State

  const [segments, setSegments] = useState({ month: '', day: '', year: '' });

  // Focus State
  const [focused, setFocused] = useState(false);

  // Hover State
  const [hovered, setHovered] = useState(false);

  // Calendar Popup Open State
  const [popupOpen, setPopupOpen] = useState(false);

  // ----- REFS ----- //

  // Popup Anchor Reference

  const anchorRef = useRef(null);

  // Month Segment Input Reference
  const monthRef = useRef(null);

  // Day Segment Input Reference
  const dayRef = useRef(null);

  // Year Segment Input Reference
  const yearRef = useRef(null);

  // ----- MEMOS ----- //

  // Synchronizes segmented values from an external date value.

  const syncFromValue = useCallback((nextValue) => {
    const normalized = normalizeDateValue(nextValue);
    if (!normalized) {
      setSegments({ month: '', day: '', year: '' });
      return;
    }

    setSegments({
      month: padStart(normalized.getMonth() + 1, 2),
      day: padStart(normalized.getDate(), 2),
      year: String(normalized.getFullYear()),
    });
  }, []);

  // Normalized date value for derived presentation.
  const normalizedValue = useMemo(() => normalizeDateValue(value), [value]);

  // Human-readable formatted date label.
  const friendlyLabel = useMemo(() => {
    if (!normalizedValue) return '';
    const formatOptions = friendlyFormatOptions && typeof friendlyFormatOptions === 'object'
      ? friendlyFormatOptions
      : {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      };
    return normalizedValue.toLocaleDateString('en-US', formatOptions);
  }, [friendlyFormatOptions, normalizedValue]);

  // Wrapper class with interactive and visual state flags.
  const wrapperClass = useMemo(() => (
    `date-picker-container${disabled ? ' disabled' : ''}${focused ? ' focused' : ''}${hybrid ? ' date-picker-hybrid' : ''}${friendlyLabel && !focused && !popupOpen ? ' date-picker-friendly-active' : ''} ${className}`.trim()
  ), [disabled, focused, hybrid, friendlyLabel, popupOpen, className]);

  // Shared class name for all segmented inputs.
  const segmentInputClassName = useMemo(() => {
    return `date-picker-input ${inputClassName}`.trim();
  }, [inputClassName]);

  // ----- EFFECTS ----- //

  // Keeps input segments synchronized with controlled value updates.

  useEffect(() => {
    syncFromValue(value);
  }, [value, syncFromValue]);

  // ----- MEMOS ----- //

  /**
   * Builds a raw date string from segmented values.
   *
   * @param {object} nextSegments - Next segment values.
   * @returns {string} Raw date value.
   */

  const buildRawValue = useCallback((nextSegments) => (
    `${nextSegments.month}${nextSegments.month ? '/' : ''}${nextSegments.day}${
      nextSegments.day ? '/' : ''
    }${nextSegments.year}`
  ), []);

  /**
   * Emits parsed and raw date updates for consumers.
   *
   * @param {object} nextSegments - Next segment values.
   */
  const emitChange = useCallback((nextSegments) => {
    const nextDate = buildDateFromParts(
      nextSegments.month,
      nextSegments.day,
      nextSegments.year
    );

    if (typeof onRawChange === 'function') {
      const rawValue = buildRawValue(nextSegments);
      onRawChange(rawValue);
    }

    if (typeof onChange === 'function') {
      onChange(nextDate);
    }
  }, [buildRawValue, onChange, onRawChange]);

  // ----- HANDLERS ----- //

  /**
   * Handles blur from any segment input and emits onBlur when focus exits the control.
   *
   * @param {FocusEvent} event - Native blur event.
   */

  const handleInputBlur = useCallback((event) => {
    setFocused(false);

    const nextFocusTarget = event.relatedTarget;
    if (nextFocusTarget instanceof Node && anchorRef.current?.contains(nextFocusTarget)) {
      return;
    }

    if (typeof onBlur === 'function') {
      const rawValue = buildRawValue(segments);
      const parsedDate = buildDateFromParts(segments.month, segments.day, segments.year);
      onBlur({ rawValue, value: parsedDate });
    }
  }, [buildRawValue, onBlur, segments]);

  /**
   * Handles input changes for a specific date segment.
   *
   * @param {string} segment - Segment key: month, day, or year.
   * @param {string} nextValue - Next raw input value.
   */
  const handleSegmentChange = useCallback((segment, nextValue) => {
    const maxLength = segment === 'year' ? 4 : 2;
    const cleaned = clampDigits(nextValue, maxLength);

    setSegments((prev) => {
      const nextSegments = { ...prev, [segment]: cleaned };
      emitChange(nextSegments);
      return nextSegments;
    });

    if (cleaned.length === maxLength) {
      if (segment === 'month') dayRef.current?.focus();
      if (segment === 'day') yearRef.current?.focus();
    }
  }, [emitChange]);

  /**
   * Handles date selection from the calendar popup.
   *
   * @param {Date|null} nextDate - Selected calendar date.
   */
  const handleCalendarSelect = useCallback((nextDate) => {
    if (!nextDate) return;
    syncFromValue(nextDate);
    if (typeof onChange === 'function') {
      onChange(nextDate);
    }
    setPopupOpen(false);
  }, [onChange, syncFromValue]);

  /**
   * Handles segment backspace navigation when a segment is empty.
   *
   * @param {string} segment - Segment key: month, day, or year.
   * @param {KeyboardEvent} event - Native keyboard event.
   */
  const handleKeyDown = useCallback((segment, event) => {
    if (event.key !== 'Backspace') return;

    const current = segments[segment];
    if (current) return;

    if (segment === 'day') {
      monthRef.current?.focus();
    } else if (segment === 'year') {
      dayRef.current?.focus();
    }
  }, [segments]);

  const resolvedStartIconColor = startIconColor || theme.foreground;
  const emptySegmentColor = theme.foregroundMuted;

  // ----- MEMOS ----- //

  /**
   * Resolves segment input text color by segment emptiness.
   *
   * @param {string} segmentValue - Current segment value.
   * @returns {object} Inline style object for the segment input.
   */

  const getSegmentInputStyle = useCallback((segmentValue) => ({
    ...inputStyle,
    color: segmentValue ? theme.foreground : emptySegmentColor,
  }), [emptySegmentColor, inputStyle, theme.foreground]);

  // ----- HANDLERS ----- //

  /**
   * Handles wrapper clicks to open the calendar popup.
   *
   * @param {MouseEvent} event - Native click event.
   */

  const handleWrapperClick = (event) => {
    if (disabled || readOnly) return;
    const target = event.target;
    if (target instanceof HTMLElement && target.tagName === 'INPUT') return;
    setPopupOpen(true);
  };

  /**
   * Handles friendly-label mouse down to focus month input.
   *
   * @param {MouseEvent} event - Native mouse event.
   */
  const handleFriendlyClick = (event) => {
    event.preventDefault();
    event.stopPropagation();
    monthRef.current?.focus();
  };

  /**
   * Handles wrapper mouse enter state.
   */
  const handleMouseEnter = () => {
    setHovered(true);
  };

  /**
   * Handles wrapper mouse leave state.
   */
  const handleMouseLeave = () => {
    setHovered(false);
  };

  // ----- RENDER ----- //

  return (
    <div
      className={wrapperClass}
      style={{
        backgroundColor: hybrid
          ? (hovered ? theme.input : 'transparent')
          : theme.input,
        color: theme.foreground,
        ...style,
      }}
      ref={anchorRef}
      role="button"
      tabIndex={-1}
      onClick={handleWrapperClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >

      {/* INNER */}
      <div className="date-picker-inner">

        {/* ICON */}
        <div className="date-picker-icon">
          <Icon
            color={resolvedStartIconColor}
            height={iconSize}
            iconKey={startIconKey}
            width={iconSize}
          />
        </div>

        {/* SEGMENTS */}
        <div className="date-picker-segments">

          {/* FRIENDLY LABEL */}
          {friendlyLabel && !focused && !popupOpen && (
            <button
              className="date-picker-friendly"
              type="button"
              style={
                friendlyFontWeight == null
                  ? undefined
                  : { fontWeight: friendlyFontWeight }
              }
              onMouseDown={handleFriendlyClick}
            >
              {friendlyLabel}
            </button>
          )}
          <input
            className={segmentInputClassName}
            style={getSegmentInputStyle(segments.month)}
            disabled={disabled}
            inputMode="numeric"
            maxLength={2}
            pattern="[0-9]*"
            placeholder="MM"
            readOnly={readOnly}
            ref={monthRef}
            value={segments.month}
            onBlur={handleInputBlur}
            onChange={(event) => handleSegmentChange('month', event.target.value)}
            onFocus={() => setFocused(true)}
            onKeyDown={(event) => handleKeyDown('month', event)}
          />

          {/* SEPARATOR */}
          <span
            className="date-picker-separator"
            style={{
              color: emptySegmentColor,
            }}
          >
            /
          </span>
          <input
            className={segmentInputClassName}
            style={getSegmentInputStyle(segments.day)}
            disabled={disabled}
            inputMode="numeric"
            maxLength={2}
            pattern="[0-9]*"
            placeholder="DD"
            readOnly={readOnly}
            ref={dayRef}
            value={segments.day}
            onBlur={handleInputBlur}
            onChange={(event) => handleSegmentChange('day', event.target.value)}
            onFocus={() => setFocused(true)}
            onKeyDown={(event) => handleKeyDown('day', event)}
          />

          {/* SEPARATOR */}
          <span
            className="date-picker-separator"
            style={{
              color: emptySegmentColor,
            }}
          >
            /
          </span>
          <input
            className={segmentInputClassName}
            style={getSegmentInputStyle(segments.year)}
            disabled={disabled}
            inputMode="numeric"
            maxLength={4}
            pattern="[0-9]*"
            placeholder="YYYY"
            readOnly={readOnly}
            ref={yearRef}
            value={segments.year}
            onBlur={handleInputBlur}
            onChange={(event) => handleSegmentChange('year', event.target.value)}
            onFocus={() => setFocused(true)}
            onKeyDown={(event) => handleKeyDown('year', event)}
          />
        </div>

        {/* END ACTION */}
        {endAction && (

          <div className="date-picker-end-action">
            {endAction}
          </div>
        )}
      </div>

      <Popup anchorRef={anchorRef} isOpen={popupOpen} setOpen={setPopupOpen}>
        <CalendarPopup value={value} onSelect={handleCalendarSelect} />
      </Popup>
    </div>
  );
};

// Dev Tools
DatePicker.displayName = 'DatePicker';

export default DatePicker;
